import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import theme from 'theme'

import type { BoxProps, FlexProps } from '@chakra-ui/react'
import type { IconProp, SizeProp } from '@fortawesome/fontawesome-svg-core'
import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import type { ReactNode } from 'react'
import type { ComponentStyleProps } from 'types'

const componentStatuses = ['error', 'primary', 'success', 'warning'] as const
type NotificationHeroStatus = (typeof componentStatuses)[number]

export type NotificationHeroStatusStyleProps<TComponentParts extends ComponentStyleProps> = {
  [key in NotificationHeroStatus]: TComponentParts
}

type Props = {
  status?: NotificationHeroStatus
  size?: SizeProp
  icon?: IconProp | string
  title: string
  message?: ReactNode
  isClosable?: boolean
  onClose?: () => void
}

const statusStyleProps: NotificationHeroStatusStyleProps<{
  container: Partial<FlexProps>
  iconBox: Partial<BoxProps>
  icon: Omit<FontAwesomeIconProps, 'icon'>
}> = {
  error: {
    container: {
      background: theme.baseComponents.card.colors.background.error,
      borderLeft: theme.baseComponents.card.border.error,
      color: theme.baseColors.text.default,
    },
    iconBox: {
      bg: 'white',
    },
    icon: {
      color: theme.baseColors.danger.main,
    },
  },
  primary: {
    container: {
      background: theme.baseComponents.card.colors.background.default,
      borderLeft: theme.baseComponents.card.border.success,
      color: theme.baseColors.text.default,
    },
    iconBox: {
      bg: theme.colors.purple['50'],
    },
    icon: {
      color: theme.colors.purple['500'],
    },
  },
  success: {
    container: {
      background: theme.colors.purple['800'],
      color: theme.colors.white,
    },
    iconBox: {
      bg: 'white',
    },
    icon: {
      color: theme.colors.white,
    },
  },
  warning: {
    container: {
      background: theme.baseComponents.card.colors.background.default,
      borderLeft: `6px solid ${theme.colors.orange['500']}`,
      color: theme.baseColors.text.default,
    },
    iconBox: {
      bg: theme.colors.orange['50'],
    },
    icon: {
      color: theme.colors.orange['500'],
    },
  },
}

const NotificationHero = ({ status = 'primary', size = 'lg', icon, title, message, isClosable, onClose }: Props) => {
  const isSmall = ['2xs', 'xs', 'sm'].includes(size)
  return (
    <Flex
      {...statusStyleProps[status].container}
      w="100%"
      justify="space-between"
      borderRadius="base"
      boxShadow="lg"
      userSelect="none"
    >
      <Flex w="100%" px={isSmall ? 4 : 6} py={5} alignItems="center">
        {icon && (
          <Flex align={typeof icon === 'string' ? 'center' : 'flex-start'} mr="12px">
            {typeof icon === 'string' && (
              <Box background={`url(${icon})`} boxSize={isSmall ? 12 : 16} borderRadius="full" />
            )}
            {typeof icon !== 'string' && message && (
              <Box
                display="grid"
                placeItems="center"
                boxSize={isSmall ? 12 : 16}
                borderRadius="full"
                {...statusStyleProps[status].iconBox}
              >
                <FontAwesomeIcon icon={icon} {...statusStyleProps[status].icon} size="2x" />
              </Box>
            )}
            {typeof icon !== 'string' && !message && <FontAwesomeIcon icon={icon} {...statusStyleProps[status].icon} />}
          </Flex>
        )}
        <Stack spacing={status === 'success' ? '2px' : '6px'}>
          <Text textStyle={message ? 'body1Bold' : 'body1'}>{title}</Text>
          {message}
        </Stack>
      </Flex>
      {isClosable && (
        <Flex p={status === 'success' ? '16px' : '24px'} align="flex-start" onClick={onClose} cursor="pointer">
          <FontAwesomeIcon icon={['far', 'xmark']} {...statusStyleProps[status].icon} />
        </Flex>
      )}
    </Flex>
  )
}

export default NotificationHero
